<template>
    <svg class="discs" viewBox="0 0 720 240" aria-hidden="true" focusable="false">
        <defs>
            <clipPath :id="`${uid}-a`"><circle ref="clipA" class="clip-a" :cy="CENTRE_Y" :r="RADIUS" /></clipPath>
            <clipPath :id="`${uid}-b`"><circle ref="clipB" class="clip-b" :cy="CENTRE_Y" :r="RADIUS" /></clipPath>
            <clipPath :id="`${uid}-c`"><circle ref="clipC" class="clip-c" :cy="CENTRE_Y" :r="RADIUS" /></clipPath>
        </defs>

        <circle ref="fillA" class="disc-a" :cy="CENTRE_Y" :r="RADIUS" :fill="DISC_A" />
        <circle ref="fillB" class="disc-b" :cy="CENTRE_Y" :r="RADIUS" :fill="DISC_B" />
        <circle ref="fillC" class="disc-c" :cy="CENTRE_Y" :r="RADIUS" :fill="DISC_C" />

        <!-- An element takes only one clip-path, so the intersections nest. -->
        <g :clip-path="`url(#${uid}-a)`">
            <g :clip-path="`url(#${uid}-b)`"><rect x="0" y="0" width="720" height="240" :fill="OVERLAP_AB" /></g>
        </g>
        <g :clip-path="`url(#${uid}-b)`">
            <g :clip-path="`url(#${uid}-c)`"><rect x="0" y="0" width="720" height="240" :fill="OVERLAP_BC" /></g>
        </g>
        <g :clip-path="`url(#${uid}-a)`">
            <g :clip-path="`url(#${uid}-b)`">
                <g :clip-path="`url(#${uid}-c)`"><rect x="0" y="0" width="720" height="240" :fill="OVERLAP_ABC" /></g>
            </g>
        </g>
    </svg>
</template>

<script setup lang="ts">
/**
 * Two discs trade places across a third that never moves, crossing at the centre where all three line up
 * and the shape reads as one colour. This step is where the calendar and the Perspective answers are
 * combined, and the overlaps are the illustration of that.
 *
 * Each overlap is painted again in its own colour rather than blended, because multiplying against the page
 * ground gives the wrong colour on a themed surface. Three intersection colours cover every region the
 * animation can produce: the outer discs only ever meet while both are on top of the static one, so their
 * pairwise overlap always sits inside it.
 *
 * Values are written per frame rather than by CSS transition, for the reason CalendarWeekLoader gives: a
 * throttled tab leaves transitions stuck and the loader looking broken.
 */
import { TYPE_COLORS } from "~vue/pages/perspectiveTypes";
import { onBeforeUnmount, onMounted, useId, useTemplateRef } from "vue";

/* The wheel's palette, so the two Perspective illustrations cannot drift apart. These are chosen colours,
   not blends -- an overlap is not the two discs' colours multiplied together. */
const DISC_A = TYPE_COLORS.ESFJ;
const DISC_B = TYPE_COLORS.ESTJ;
const DISC_C = TYPE_COLORS.ESTP;
const OVERLAP_AB = TYPE_COLORS.ISTP;
const OVERLAP_BC = TYPE_COLORS.ENTJ;
const OVERLAP_ABC = TYPE_COLORS.ENFP;

const CENTRE_Y = 120;
const RADIUS = 70;

/** The three resting positions across the stage. The outer discs swap between them; the middle one holds. */
const LEFT = 180;
const CENTRE = 360;
const RIGHT = 540;

/** One there-and-back swap of the outer discs. */
const CYCLE_MS = 1800;

/* Reduced motion parks the outer discs halfway, which is the nearest still frame that shows what the
   animation is about: each one overlapping the middle disc, without any of the movement. */
const RESTING_LEFT = 270;
const RESTING_RIGHT = 450;

const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;

const uid = useId();

const fillA = useTemplateRef<SVGCircleElement>("fillA");
const fillB = useTemplateRef<SVGCircleElement>("fillB");
const fillC = useTemplateRef<SVGCircleElement>("fillC");
const clipA = useTemplateRef<SVGCircleElement>("clipA");
const clipB = useTemplateRef<SVGCircleElement>("clipB");
const clipC = useTemplateRef<SVGCircleElement>("clipC");

/** A disc and the clip circle that cuts its overlaps move together, so every intersection stays exact. */
function setX(fill: SVGCircleElement | null, clip: SVGCircleElement | null, x: number): void {
    const cx = x.toFixed(2);
    fill?.setAttribute("cx", cx);
    clip?.setAttribute("cx", cx);
}

/** A starts on the right and B always holds the centre, so only A's and C's positions vary. */
function place(a: number, c: number): void {
    setX(fillA.value, clipA.value, a);
    setX(fillB.value, clipB.value, CENTRE);
    setX(fillC.value, clipC.value, c);
}

/** Pure in `elapsed`, so it renders the same however coarsely the clock is ticking. */
function draw(elapsed: number): void {
    const within = (elapsed % CYCLE_MS) / CYCLE_MS;
    /* Out on the first half of the cycle, back on the second. */
    const swapped = within < 0.5 ? easeInOutSine(within / 0.5) : 1 - easeInOutSine((within - 0.5) / 0.5);
    place(RIGHT + (LEFT - RIGHT) * swapped, LEFT + (RIGHT - LEFT) * swapped);
}

let frame: number | null = null;

onMounted(() => {
    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        place(RESTING_RIGHT, RESTING_LEFT);
        return;
    }

    /* Painted before the clock is involved, so the discs are on screen the moment the loader is shown
       rather than a frame later. */
    draw(0);

    const start = performance.now();
    const tick = (now: number): void => {
        draw(now - start);
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
    if (frame !== null) {
        cancelAnimationFrame(frame);
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .discs {
        @apply block w-[460px] max-w-full;
    }
}
</style>
